import React, { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./GroupProfile.css";
import initialGroupBannerImage from '../assets/novas-banner-template.jpeg';
import novasLogo from '../assets/novas-logo.png';
import { getGroupData, getGroupsData, saveGroupData, deleteGroup, getStudentData } from '../assets/data';
import { getLessonsForGroup, getLessonsFromLocalStorage } from '../utils/lessonsService';
import { useGroupDictionary } from '../hooks/useGroupDictionary';
import DictionaryTab from './GroupProfile/DictionaryTab';
import { useGroupMaterials } from '../hooks/useGroupMaterials';
import MaterialsTab from './GroupProfile/MaterialsTab';
import MaterialPreviewModal from './GroupProfile/MaterialPreviewModal';
import { useGroupAIRecommendations } from '../hooks/useGroupAIRecommendations';
import { useGroupHomework } from '../hooks/useGroupHomework';
import HomeworkTab from './GroupProfile/HomeworkTab';
import HomeworkModal from './GroupProfile/HomeworkModal';
import { useGroupUpcomingLessons } from '../hooks/useGroupUpcomingLessons';
import UpcomingLessonsTab from './GroupProfile/UpcomingLessonsTab';
import { useGroupLessons } from '../hooks/useGroupLessons';
import LessonsTab from './GroupProfile/LessonsTab';
import CopyLessonModal from './GroupProfile/CopyLessonModal';

// Функция для безопасного создания URL из файла
const createSafeObjectURL = (file) => {
  try {
    if (file && file instanceof File) {
      return URL.createObjectURL(file);
    }
    return null;
  } catch (error) {
    console.error('Error creating object URL:', error);
    return null;
  }
};

function GroupProfile({ isNew }) {
  const { groupId } = useParams();
  const navigate = useNavigate();



  const [groupData, setGroupData] = useState(() => {
    if (groupId === "new" || isNew) {
      return {
        id: null,
        slug: '',
        name: 'Group Name',
        email: '',
        phone: '',
        dob: '',
        contactEmail: '',
        contactPhone: '',
        proficiency: 'A1 - Elementary',
        experience: '',
        strengthsWeaknesses: '',
        pricePerStudent: '',
        status: 'active',
        goals: '',
        preferences: '',
        hobbies: '',
        vacationFrom: '',
        vacationTo: '',
        sickLeaveFrom: '',
        sickLeaveTo: '',
        pausedResumeDate: '',
        lessons: [],
        materials: [],
        links: [],
        bannerImage: initialGroupBannerImage,
      };
    }

    const group = getGroupData(groupId);
    return group ? {
      ...group,
      lessons: group.lessons || [],
    } : {
      id: null,
      slug: 'not-found',
      name: 'Group Name',
      email: '',
      phone: '',
      dob: '',
      lessons: [],
      materials: [],
      links: [],
      bannerImage: initialGroupBannerImage,
    };
  });

  // Состояние для принудительного обновления статистики
  const [statsUpdateTrigger, setStatsUpdateTrigger] = useState(0);

  // Функция для расчета статистики группы (как в Home)
  const calculateGroupStats = useMemo(() => {
    if (!groupId || groupId === 'new') {
      return {
        lessonsThisWeek: 0,
        lessonsThisMonth: 0,
        moneyThisMonth: 0,
        plannedIncome: 0
      };
    }

    const allLessons = getLessonsFromLocalStorage();
    if (allLessons.length === 0) {
      return {
        lessonsThisWeek: 0,
        lessonsThisMonth: 0,
        moneyThisMonth: 0,
        plannedIncome: 0
      };
    }

    try {
      const currentGroupData = getGroupData(groupId);
      
      if (!currentGroupData) {
        return {
          lessonsThisWeek: 0,
          lessonsThisMonth: 0,
          moneyThisMonth: 0,
          plannedIncome: 0
        };
      }

      // Функция для парсинга цены (как в Home)
      const parsePrice = (priceStr) => {
        if (!priceStr) return 0;
        const match = priceStr.match(/\d+(\.\d+)?/);
        return match ? parseFloat(match[0]) : 0;
      };

      const pricePerStudent = parsePrice(currentGroupData.pricePerStudent);

      // Уроки на этой неделе (как в Home)
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
      
      const lessonsThisWeek = allLessons.reduce((count, lesson) => {
        if (!lesson.date || lesson.type === 'event') return count;
        if (String(lesson.groupId) !== String(groupId)) return count;
        const lessonDate = new Date(lesson.date);
        if (lessonDate >= startOfWeek && lessonDate <= endOfWeek) {
          // Calculate lesson duration in hours
          const startTime = lesson.from ? lesson.from.split(':') : ['0', '0'];
          const endTime = lesson.to ? lesson.to.split(':') : ['1', '0'];
          const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
          const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
          const durationMinutes = endMinutes - startMinutes;
          const durationHours = durationMinutes / 60;
          return count + Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour
        }
        return count;
      }, 0);

      // Уроки в этом месяце (как в Home)
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      const lessonsThisMonth = allLessons.reduce((count, lesson) => {
        if (!lesson.date || lesson.type === 'event') return count;
        if (String(lesson.groupId) !== String(groupId)) return count;
        const lessonDate = new Date(lesson.date);
        if (lessonDate.getMonth() === currentMonth && lessonDate.getFullYear() === currentYear) {
          // Calculate lesson duration in hours
          const startTime = lesson.from ? lesson.from.split(':') : ['0', '0'];
          const endTime = lesson.to ? lesson.to.split(':') : ['1', '0'];
          const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
          const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
          const durationMinutes = endMinutes - startMinutes;
          const durationHours = durationMinutes / 60;
          return count + Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour
        }
        return count;
      }, 0);

      // Реальный доход за месяц
      const moneyThisMonth = allLessons.reduce((sum, lesson) => {
        if (!lesson.date || lesson.type === 'event' || lesson.status !== 'done') return sum;
        if (String(lesson.groupId) !== String(groupId)) return sum;
        const lessonDate = new Date(lesson.date);
        if (lessonDate.getMonth() !== currentMonth || lessonDate.getFullYear() !== currentYear) return sum;
        
        // Calculate lesson duration in hours
        const startTime = lesson.from ? lesson.from.split(':') : ['0', '0'];
        const endTime = lesson.to ? lesson.to.split(':') : ['1', '0'];
        const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
        const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = durationMinutes / 60;
        const lessonCount = Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour
        
        return sum + (pricePerStudent * lessonCount);
      }, 0);

      // Планируемый доход за месяц (все запланированные уроки)
      const plannedIncome = allLessons.reduce((sum, lesson) => {
        if (!lesson.date || lesson.type === 'event') return sum;
        if (String(lesson.groupId) !== String(groupId)) return sum;
        const lessonDate = new Date(lesson.date);
        if (lessonDate.getMonth() !== currentMonth || lessonDate.getFullYear() !== currentYear) return sum;
        
        // Calculate lesson duration in hours
        const startTime = lesson.from ? lesson.from.split(':') : ['0', '0'];
        const endTime = lesson.to ? lesson.to.split(':') : ['1', '0'];
        const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
        const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = durationMinutes / 60;
        const lessonCount = Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour
        
        return sum + (pricePerStudent * lessonCount);
      }, 0);

      return {
        lessonsThisWeek,
        lessonsThisMonth,
        moneyThisMonth,
        plannedIncome
      };
    } catch (e) {
      console.error('Error calculating group stats:', e);
      return {
        lessonsThisWeek: 0,
        lessonsThisMonth: 0,
        moneyThisMonth: 0,
        plannedIncome: 0
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, statsUpdateTrigger]);

  // Обновляем статистику при изменении localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      setStatsUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Также обновляем при изменении localStorage в том же окне
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (key === 'novas_lessons_data' || key === 'novas_groups_data') {
        handleStorageChange();
      }
    };

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  const [bannerImageUrl, setBannerImageUrl] = useState(initialGroupBannerImage);
  const fileInputRef = useRef(null);

  const [activeSectionTab, setActiveSectionTab] = useState('Lessons');
  const [groupHomework, setStudentHomework] = useState([]);
  const [groupLessons, setStudentLessons] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [upcomingLessons, setUpcomingLessons] = useState([]);

  const [participants, setParticipants] = useState(groupData.participants || []);
  const [showStudentSelector, setShowStudentSelector] = useState(false);

  const {
    dictionarySearchTerm, setDictionarySearchTerm,
    isAddWordModalOpen, setIsAddWordModalOpen,
    newWord, setNewWord,
    dictionaryEntries, setDictionaryEntries,
    editingWordId, setEditingWordId,
    editingWordData, setEditingWordData,
    handleAddWordClick, handleAddWordSubmit, handleDeleteWord, playWordAudio,
  } = useGroupDictionary(groupId);

  // Modal states
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customModalConfig, setCustomModalConfig] = useState({});
  const showCustomModalConfig = (config) => {
    setCustomModalConfig(config);
    setShowCustomModal(true);
  };

  const hideCustomModal = () => {
    setShowCustomModal(false);
  };

  const handleModalConfirm = () => {
    if (customModalConfig.onConfirm) {
      customModalConfig.onConfirm();
    }
    hideCustomModal();
  };

  const {
    newMaterialLink, setNewMaterialLink,
    newMaterialFile,
    materialType, setMaterialType,
    materialFileInputRef,
    materialSearchTerm, setMaterialSearchTerm,
    editingMaterialId,
    editingMaterialName, setEditingMaterialName,
    previewMaterial,
    isDragOver,
    filteredMaterials,
    handleAddLink, handleFileChange,
    handleDragOver, handleDragEnter, handleDrop, handleDragLeave,
    handleDeleteMaterial, handleEditMaterialName, handleSaveMaterialName, handleCancelEditMaterial,
    handleMaterialPreview, handleClosePreview,
  } = useGroupMaterials({ groupData, setGroupData, showCustomModalConfig, setShowCustomModal });

  useEffect(() => {
          if (groupId && groupId !== "new" && !isNew && getGroupData(groupId)) {
        const existingStudent = getGroupData(groupId);
      setGroupData(prev => ({
        ...prev,
        name: existingStudent.name || "",
        dob: existingStudent.dob || "",
        contactEmail: existingStudent.contactEmail || "",
        contactPhone: existingStudent.contactPhone || "",
        proficiency: existingStudent.proficiency || "A1 - Elementary",
        experience: existingStudent.experience || "",
        strengthsWeaknesses: existingStudent.strengthsWeaknesses || "",
        price: existingStudent.price || "",
        status: existingStudent.status ? existingStudent.status.toLowerCase().replace(/\s/g, '_') : "active",
        goals: existingStudent.goals || "",
        preferences: existingStudent.preferences || "",
        hobbies: existingStudent.hobbies || "",
        vacationFrom: existingStudent.vacationFrom || "",
        vacationTo: existingStudent.vacationTo || "",
        sickLeaveFrom: existingStudent.sickLeaveFrom || "",
        sickLeaveTo: existingStudent.sickLeaveTo || "",
        pausedResumeDate: existingStudent.pausedResumeDate || "",
        lessons: existingStudent.lessons || [],
        materials: existingStudent.materials || [],
      }));

      // Загружаем уроки для этого студента
      getLessonsForGroup(groupId).then(lessons => {
        console.log('=== LOADING LESSONS FOR GROUP ===');
        console.log('groupId:', groupId);
        console.log('Loaded lessons:', lessons);
        console.log('Lessons with content:', lessons.map(lesson => ({
          id: lesson.id,
          title: lesson.title,
          hasContainers: !!lesson.containers,
          hasWhiteboards: !!lesson.whiteboards,
          hasEditorContents: !!lesson.editorContents,
          hasWhiteboardContents: !!lesson.whiteboardContents,
          containers: lesson.containers,
          editorContents: lesson.editorContents
        })));
        setStudentLessons(lessons);
      }).catch(error => {
        console.error('Error loading lessons:', error);
        setStudentLessons([]);
      });
    } else if (groupId === "new" || isNew) {
      // Для нового студента не перезаписываем данные, оставляем начальные значения из useState
      setBannerImageUrl(initialGroupBannerImage);
      setStudentLessons([]);
    }
  }, [groupId, isNew]);

  // Загружаем homework студента
  useEffect(() => {
    const loadStudentHomework = () => {
      const homeworkData = [];
      
      // Получаем уроки этой группы
      getLessonsForGroup(groupId).then(groupLessons => {
        const groupLessonIds = groupLessons.map(lesson => lesson.id);
        console.log('Group lessons:', groupLessons);
        console.log('Group lesson IDs:', groupLessonIds);
        
        // Ищем все homework в localStorage
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('homework_')) {
            try {
              const homework = JSON.parse(localStorage.getItem(key));
              // Проверяем, что homework назначен этой группе ИЛИ связан с уроком этой группы
              const isAssignedToGroup = homework.groupId === groupId;
              const isRelatedToGroupLesson = homework.lessonId && groupLessonIds.includes(homework.lessonId);
              
              console.log(`Homework ${key}:`, {
                homeworkId: key.replace('homework_', ''),
                groupId: homework.groupId,
                lessonId: homework.lessonId,
                isAssignedToGroup,
                isRelatedToGroupLesson,
                willShow: isAssignedToGroup || isRelatedToGroupLesson
              });
              
              if (isAssignedToGroup || isRelatedToGroupLesson) {
                homeworkData.push({
                  id: key.replace('homework_', ''),
                  ...homework
                });
              }
            } catch (error) {
              console.error('Error parsing homework data:', error);
            }
          }
        }
        
        // Сортируем по дате сдачи
        const sortedHomework = homeworkData.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        console.log('Final homework for group:', sortedHomework);
        setStudentHomework(sortedHomework);
      }).catch(error => {
        console.error('Error loading group lessons for homework:', error);
        setStudentHomework([]);
      });
    };
    
    loadStudentHomework();
  }, [groupId]);

  // Функция для обновления upcoming lessons
  const updateUpcomingLessons = () => {
    if (groupId && groupId !== "new" && !isNew) {
      const allLessonsData = getLessonsFromLocalStorage();
      const allGroupLessons = allLessonsData.filter(lesson => lesson.groupId === groupId);
      
      // Фильтруем только предстоящие уроки (с датой в будущем или сегодня)
      const upcoming = allGroupLessons.filter(lesson => {
        if (!lesson.date) return false;
        
        const lessonDate = new Date(lesson.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Сбрасываем время до начала дня
        
        return lessonDate >= today;
      });
      
      // Сортируем по дате и берем только 2 ближайших
      const sortedLessons = upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
      const nearestLessons = sortedLessons.slice(0, 2);
      
      setUpcomingLessons(nearestLessons);
    }
  };

  const { aiRecommendations, generateAIRecommendations } = useGroupAIRecommendations(groupData);

  const {
    homeworkFilter, setHomeworkFilter,
    selectedHomework,
    handleOpenHomework, handleCloseHomework, handleCheckHomework,
  } = useGroupHomework({ groupId, navigate, groupHomework, setStudentLessons, updateUpcomingLessons });

  const {
    weekOffset, setWeekOffset,
    selectedDayIndex, setSelectedDayIndex,
    handleLessonStatusChange: handleUpcomingLessonStatusChange,
  } = useGroupUpcomingLessons({ groupId, setGroupData, setStudentLessons, updateUpcomingLessons });

  const {
    lessonSearchTerm, setLessonSearchTerm,
    deletingLessonId,
    showCopyModal, setShowCopyModal,
    lessonToCopy,
    copyTargetStudent, setCopyTargetStudent,
    copyModalGroups,
    copyLessonSubmitting,
    copyDate, setCopyDate,
    filteredLessons,
    handleCreateLesson, handleStartLesson, handleDeleteLesson, handleLessonStatusChange,
    handleCopyLesson, confirmCopyLesson,
  } = useGroupLessons({ groupId, navigate, groupLessons, setStudentLessons, showCustomModalConfig, setShowCustomModal });

  // Добавляем эффект для обновления уроков при изменении localStorage
  useEffect(() => {
    if (groupId && groupId !== "new" && !isNew) {
      const handleStorageChange = () => {
        getLessonsForGroup(groupId).then(lessons => {
          setStudentLessons(lessons);
          updateUpcomingLessons(); // Обновляем upcoming lessons
        }).catch(error => {
          console.error('Error loading lessons:', error);
        });
      };

      // Слушаем изменения в localStorage
      window.addEventListener('storage', handleStorageChange);
      
      // Также проверяем каждые 3 секунды на случай, если изменения происходят в том же окне
      const interval = setInterval(handleStorageChange, 3000);
      
      // Инициализируем upcoming lessons
      updateUpcomingLessons();
      
      // Генерируем AI-рекомендации при загрузке
      setTimeout(() => {
        generateAIRecommendations();
      }, 200);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, isNew]);

  // Дополнительный эффект для отслеживания изменений в localStorage в том же окне
  useEffect(() => {
    const handleLocalStorageChange = (e) => {
      if (e.key === 'novas_lessons_data') {
        // Небольшая задержка для обеспечения обновления данных
        setTimeout(() => {
          getLessonsForGroup(groupId).then(lessons => {
            setStudentLessons(lessons);
            updateUpcomingLessons();
          }).catch(error => {
            console.error('Error loading lessons:', error);
          });
        }, 100);
      }
    };

    // Слушаем изменения localStorage в том же окне
    window.addEventListener('storage', handleLocalStorageChange);
    
    // Также перехватываем setItem для отслеживания изменений в том же окне
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (key === 'novas_lessons_data') {
        setTimeout(() => {
          getLessonsForGroup(groupId).then(lessons => {
            setStudentLessons(lessons);
            updateUpcomingLessons();
          }).catch(error => {
            console.error('Error loading lessons:', error);
          });
        }, 100);
      }
    };

    return () => {
      window.removeEventListener('storage', handleLocalStorageChange);
      localStorage.setItem = originalSetItem;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const proficiencyLevels = [
    "A1 - Elementary",
    "A2 - Pre-Intermediate",
    "A2+ - Pre-Intermediate+",
    "B1 - Intermediate",
    "B1+ - Intermediate+",
    "B2 - Upper-Intermediate",
    "B2+ - Upper-Intermediate+",
    "C1 - Advanced",
    "C1+ - Advanced+",
    "C2 - Proficient"
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "on_vacation", label: "On Vacation" },
    { value: "sick_leave", label: "Sick Leave" },
    { value: "paused", label: "Paused" }
  ];

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setGroupData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Генерируем рекомендации при изменении ключевых полей
    if (['experience', 'goals', 'preferences', 'hobbies', 'strengthsWeaknesses'].includes(name)) {
      setTimeout(() => {
        generateAIRecommendations();
      }, 100);
    }
  };

  const handleStatusChange = (e) => {
    const { value } = e.target;
    setGroupData(prev => ({
      ...prev,
      status: value,
      vacationFrom: (value === "on_vacation") ? prev.vacationFrom : "",
      vacationTo: (value === "on_vacation") ? prev.vacationTo : "",
      sickLeaveFrom: (value === "sick_leave") ? prev.sickLeaveFrom : "",
      sickLeaveTo: (value === "sick_leave") ? prev.sickLeaveTo : "",
      pausedResumeDate: value === "paused" ? prev.pausedResumeDate : ""
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const newImageUrl = createSafeObjectURL(file);
      setBannerImageUrl(newImageUrl);
    }
  };

  const handleBannerUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const generateSlug = (name) => {
      return name.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, '');
    };

    if (groupId === "new" || isNew) {
      const newSlug = generateSlug(groupData.name);
              const currentData = getGroupsData();
      
              if (newSlug && !currentData[newSlug]) {
          saveGroupData({ ...groupData, id: newSlug });
        console.log('New group added:', newSlug);
        showCustomModalConfig({
          title: 'Success!',
          message: `New group "${groupData.name}" has been saved successfully!`,
          type: 'success',
          showCancel: false,
          confirmText: 'OK'
        });
      } else {
        showCustomModalConfig({
          title: 'Error',
          message: 'Student name is required or a group with this name already exists.',
          type: 'error',
          showCancel: false,
          confirmText: 'OK'
        });
      }
    } else {
              saveGroupData({ ...groupData, id: groupId });
      console.log('Student updated:', groupId);
      showCustomModalConfig({
        title: 'Success!',
        message: `Student "${groupData.name}" has been updated successfully!`,
        type: 'success',
        showCancel: false,
        confirmText: 'OK'
      });
    }
  };

  const handleDeleteProfile = () => {
    if (groupId === "new" || isNew) {
      showCustomModalConfig({
        title: 'Cannot Delete',
        message: 'Cannot delete a new group profile.',
        type: 'warning',
        showCancel: false,
        confirmText: 'OK'
      });
      return;
    }

    showCustomModalConfig({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete ${groupData.name}'s profile? This action cannot be undone.`,
      type: 'warning',
      onConfirm: () => {
        deleteGroup(groupId);
        console.log('Student deleted:', groupId);
        navigate('/students');
      },
      showCancel: true,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
  };

  const handleAddParticipant = (studentId) => {
    const studentData = getStudentData()[studentId];
    if (studentData && !participants.find(p => p.id === studentId)) {
      const newParticipant = {
        id: studentId,
        name: studentData.name,
        email: studentData.contactEmail,
        phone: studentData.contactPhone
      };
      setParticipants([...participants, newParticipant]);
      setGroupData(prev => ({
        ...prev,
        participants: [...participants, newParticipant]
      }));
    }
    setShowStudentSelector(false);
  };

  const handleRemoveParticipant = (studentId) => {
    const updatedParticipants = participants.filter(p => p.id !== studentId);
    setParticipants(updatedParticipants);
    setGroupData(prev => ({
      ...prev,
      participants: updatedParticipants
    }));
  };


  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div className={`group-profile-page${detailsOpen ? ' details-open' : ''}`}>
      <div
        className="profile-banner"
        style={{ backgroundImage: `url(${bannerImageUrl})` }}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
          accept="image/*"
        />
        <div className="banner-top-left-actions">
          <button className="back-button" onClick={() => navigate('/students')}>
            <span className="back-button-icon">
              <svg width="24" height="24" viewBox="0 0 100 100" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="m97.499855 59.5758209c0 14.2470169-11.5494766 25.7964935-25.7964935 25.7964935h-14.4460335c-2.3745003 0-4.2994156-1.9249115-4.2994156-4.2994156 0-2.3744965 1.9249153-4.2994156 4.2994156-4.2994156h14.2310677c9.4980011 0 17.1976624-7.6996536 17.1976624-17.1976624s-7.6996613-17.1976624-17.1976624-17.1976624h-52.9258137l13.7151318 11.5224342c1.816494 1.5315552 2.0474854 4.2456856.5159302 6.0621758-1.5315495 1.8164978-4.2456799 2.0474892-6.0621738.515934l-22.7009121-19.0464133c-1.8164933-1.5292511-2.0493481-4.2415123-.5200972-6.0580063.1581917-.1879044.3321936-.361908.5200973-.5200996l22.7009163-19.2183876c1.8283691-1.5315533 4.5521183-1.2909393 6.0836735.5374269s1.2909393 4.5521183-.5374298 6.0836735l-13.7151362 11.4364443h52.9258118c14.2465134-.1188049 25.8919067 11.3339691 26.010704 25.5804825.0008488.1006698.0010929.2013283.0007572.3019981z"/>
              </svg>
            </span>
            <span className="back-button-text">Return</span>
          </button>
          <button 
            type="button" 
            className="save-button banner-action" 
            onClick={handleSubmit}
            aria-label="Save Profile"
          >
            <span className="banner-action-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </span>
            <span className="banner-action-text">Save</span>
          </button>
          {groupId !== "new" && (
            <button 
              type="button" 
              className="delete-profile-button banner-action" 
              onClick={handleDeleteProfile}
              aria-label="Delete Profile"
            >
              <span className="banner-action-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                </svg>
              </span>
              <span className="banner-action-text">Delete</span>
            </button>
          )}
          <button className="change-cover-button" onClick={handleBannerUploadClick}>
            <span className="change-cover-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 6H17.82L15.39 3.59C15.01 3.22 14.52 3 14 3H10C9.48 3 8.99 3.22 8.62 3.59L6.18 6H3C1.9 6 1 6.9 1 8V20C1 21.1 1.9 22 3 22H21C22.1 22 23 21.1 23 20V8C23 6.9 22.1 6 21 6ZM12 18C9.79 18 8 16.21 8 14C8 11.79 9.79 10 12 10C14.21 10 16 11.79 16 14C16 16.21 14.21 18 12 18Z"/>
              </svg>
            </span>
            <span className="change-cover-text">Change Cover</span>
          </button>
        </div>
        <h2 className="group-name-banner-input">{groupData.name}</h2>
        <img src={novasLogo} alt="Novas Logo" className="novas-banner-logo" />
      </div>

      <div className="group-details-slider">
        <button className="details-toggle" onClick={() => setDetailsOpen(v => !v)}>
          <span className={`chev${detailsOpen ? ' open' : ''}`}></span>
          <span className="details-title">Group details</span>
          <span className="details-sub">general info, goals, participants</span>
        </button>
        <div className={`details-panel${detailsOpen ? ' expanded' : ' collapsed'}`}>
      <div className="group-details-container">
        <div className="details-column">
          <h3>Name</h3>
          <input
            type="text"
            name="name"
            value={groupData.name}
            onChange={handleFieldChange}
            className="profile-input"
          />

          <h3>English Proficiency Level</h3>
          <select
            name="proficiency"
            value={groupData.proficiency}
            onChange={handleFieldChange}
            className="profile-select"
          >
            {proficiencyLevels.map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>

          <h3>Date of Birth</h3>
          <input
            type="date"
            name="dob"
            value={groupData.dob}
            onChange={handleFieldChange}
            className="profile-input"
          />

          <h3>Price per student</h3>
          <input
            type="text"
            name="pricePerStudent"
            value={groupData.pricePerStudent}
            onChange={handleFieldChange}
            className="profile-input"
          />

          <h3>Status</h3>
          <select
            name="status"
            value={groupData.status}
            onChange={handleStatusChange}
            className="profile-select"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          {groupData.status === "on_vacation" && (
            <div className="conditional-dates">
              <label>Vacation From:</label>
              <input
                type="date"
                name="vacationFrom"
                value={groupData.vacationFrom}
                onChange={handleFieldChange}
                className="profile-input"
              />
              <label>Vacation To:</label>
              <input
                type="date"
                name="vacationTo"
                value={groupData.vacationTo}
                onChange={handleFieldChange}
                className="profile-input"
              />
            </div>
          )}

          {groupData.status === "sick_leave" && (
            <div className="conditional-dates">
              <label>Sick Leave From:</label>
              <input
                type="date"
                name="sickLeaveFrom"
                value={groupData.sickLeaveFrom}
                onChange={handleFieldChange}
                className="profile-input"
              />
              <label>Sick Leave To:</label>
              <input
                type="date"
                name="sickLeaveTo"
                value={groupData.sickLeaveTo}
                onChange={handleFieldChange}
                className="profile-input"
              />
            </div>
          )}

          {groupData.status === "paused" && (
            <div className="conditional-dates">
              <label>Resume Date:</label>
              <input
                type="date"
                name="pausedResumeDate"
                value={groupData.pausedResumeDate}
                onChange={handleFieldChange}
                className="profile-input"
              />
            </div>
          )}
        </div>

        <div className="details-column">
          <h3>Experience</h3>
          <textarea
            name="experience"
            value={groupData.experience}
            onChange={handleFieldChange}
            className="profile-textarea"
            rows="3"
          />

          <h3>Goals</h3>
          <textarea
            name="goals"
            value={groupData.goals}
            onChange={handleFieldChange}
            className="profile-textarea"
            rows="3"
          />

          <h3>Preferences</h3>
          <textarea
            name="preferences"
            value={groupData.preferences}
            onChange={handleFieldChange}
            className="profile-textarea"
            rows="3"
          />

          <h3>Hobbies</h3>
          <textarea
            name="hobbies"
            value={groupData.hobbies}
            onChange={handleFieldChange}
            className="profile-textarea"
            rows="3"
          />

          <h3>Strengths/Weaknesses</h3>
          <textarea
            name="strengthsWeaknesses"
            value={groupData.strengthsWeaknesses}
            onChange={handleFieldChange}
            className="profile-textarea"
            rows="3"
          />
        </div>

        <div className="details-column ai-recommendations-column">
          <h3>
            <svg id="Master_Line" enableBackground="new 0 0 512 512" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
              <defs>
                <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#f093fb' }} />
                  <stop offset="100%" style={{ stopColor: '#f5576c' }} />
                </linearGradient>
              </defs>
              <g>
                <g>
                  <path d="m327.71 301.59c-126-19.98-130.85-170.94-130.84-194.41.01 23.47-4.85 174.43-130.84 194.41 126 19.98 130.85 170.94 130.84 194.41-.01-23.47 4.85-174.43 130.84-194.41z" fill="url(#iconGradient)"/>
                </g>
                <g>
                  <path d="m348.31 104.12c-57.11-9.05-59.31-77.48-59.31-88.12 0 10.64-2.2 79.07-59.31 88.12 57.11 9.06 59.31 77.49 59.31 88.12 0-10.63 2.2-79.06 59.31-88.12z" fill="url(#iconGradient)"/>
                </g>
                <g>
                  <path d="m445.97 233.86c-65.51-10.39-68.04-88.88-68.03-101.08.01 12.2-2.52 90.69-68.03 101.08 65.51 10.39 68.04 88.88 68.03 101.08-.01-12.2 2.52-90.69 68.03-101.08z" fill="url(#iconGradient)"/>
                </g>
              </g>
            </svg>
            AI Recommendations
          </h3>
          
          {Object.keys(aiRecommendations).length === 0 ? (
            <p className="no-recommendations">Fill in group information to get AI recommendations.</p>
          ) : (
            <div className="recommendations-list">
              {aiRecommendations.experience && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.experience }}></p>}
              {aiRecommendations.goals && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.goals }}></p>}
              {aiRecommendations.preferences && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.preferences }}></p>}
              {aiRecommendations.hobbies && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.hobbies }}></p>}
              {aiRecommendations.strengthsWeaknesses && <p dangerouslySetInnerHTML={{ __html: aiRecommendations.strengthsWeaknesses }}></p>}
            </div>
          )}
        </div>

        <div className="details-column participants-column">
          <h3>Participants</h3>
          <div className="participants-list">
            {participants.map((participant) => (
              <div key={participant.id} className="participant-item">
                <div className="participant-info">
                  <div className="participant-name">{participant.name}</div>
                  <div className="participant-contact">
                    <span className="participant-email">{participant.email}</span>
                    <span className="participant-phone">{participant.phone}</span>
                  </div>
                </div>
                <div className="participant-actions">
                  <button 
                    className="participant-action-btn view-btn"
                    onClick={() => navigate(`/students/${participant.id}`)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button 
                    className="participant-action-btn remove-btn"
                    onClick={() => handleRemoveParticipant(participant.id)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
            {participants.length === 0 && (
              <div className="no-participants">
                <p>No participants added yet</p>
              </div>
            )}
          </div>
          <button 
            className="add-participant-btn"
            onClick={() => setShowStudentSelector(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Add Student
          </button>
          {/* floating fab stack moved to page root */}
        </div>
      </div>
        </div>
      </div>

      {/* Student Stats Overview */}
      <div className="group-stats-overview section-card">
        <div className="stats-grid">
                          <div className="stat-card">
                  <div className="stat-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="stat-content">
                    <p className="stat-value">{calculateGroupStats.lessonsThisWeek}</p>
                    <h3>Lessons this week</h3>
                  </div>
                </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 2V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 2V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-value">{calculateGroupStats.lessonsThisMonth}</p>
              <h3>Lessons this month</h3>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <p className="stat-value">${calculateGroupStats.moneyThisMonth}</p>
              <h3>Money this month</h3>
            </div>
          </div>
          
                          <div className="stat-card">
                  <div className="stat-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="stat-content">
                    <p className="stat-value">${calculateGroupStats.plannedIncome}</p>
                    <h3>Planned income</h3>
                  </div>
                </div>
        </div>
      </div>

      <div className="lessons-dictionary-materials-section">
        <div className="section-tabs-navigation">
          <button
            className={`section-tab-button ${activeSectionTab === 'Lessons' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Lessons')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 19.5C4 18.1193 5.11929 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.5 2H20V22H6.5C5.11929 22 4 20.8807 4 19.5V4.5C4 3.11929 5.11929 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Lessons
          </button>
          <button
            className={`section-tab-button ${activeSectionTab === 'Homework' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Homework')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9,22 9,12 15,12 15,22"/>
            </svg>
            Homework
          </button>
          <button
            className={`section-tab-button ${activeSectionTab === 'Dictionary' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Dictionary')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Dictionary
          </button>
          <button
            className={`section-tab-button ${activeSectionTab === 'Materials' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Materials')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Materials
          </button>
        </div>

        <div className="section-content">
          {activeSectionTab === 'Lessons' && (
            <LessonsTab
              groupId={groupId}
              navigate={navigate}
              lessonSearchTerm={lessonSearchTerm}
              setLessonSearchTerm={setLessonSearchTerm}
              filteredLessons={filteredLessons}
              handleStartLesson={handleStartLesson}
              handleLessonStatusChange={handleLessonStatusChange}
              handleCopyLesson={handleCopyLesson}
              handleDeleteLesson={handleDeleteLesson}
              deletingLessonId={deletingLessonId}
            />
          )}
          {activeSectionTab === 'Homework' && (
            <HomeworkTab
              homeworkFilter={homeworkFilter}
              setHomeworkFilter={setHomeworkFilter}
              groupHomework={groupHomework}
              handleOpenHomework={handleOpenHomework}
              handleCheckHomework={handleCheckHomework}
            />
          )}
          {activeSectionTab === 'Dictionary' && (
            <DictionaryTab
              dictionaryEntries={dictionaryEntries}
              dictionarySearchTerm={dictionarySearchTerm}
              setDictionarySearchTerm={setDictionarySearchTerm}
              handleAddWordClick={handleAddWordClick}
              isAddWordModalOpen={isAddWordModalOpen}
              setIsAddWordModalOpen={setIsAddWordModalOpen}
              newWord={newWord}
              setNewWord={setNewWord}
              handleAddWordSubmit={handleAddWordSubmit}
              editingWordId={editingWordId}
              setEditingWordId={setEditingWordId}
              editingWordData={editingWordData}
              setEditingWordData={setEditingWordData}
              setDictionaryEntries={setDictionaryEntries}
              playWordAudio={playWordAudio}
              handleDeleteWord={handleDeleteWord}
            />
          )}
          {activeSectionTab === 'Materials' && (
            <MaterialsTab
              materialType={materialType}
              setMaterialType={setMaterialType}
              newMaterialLink={newMaterialLink}
              setNewMaterialLink={setNewMaterialLink}
              handleAddLink={handleAddLink}
              isDragOver={isDragOver}
              handleDragEnter={handleDragEnter}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              newMaterialFile={newMaterialFile}
              materialFileInputRef={materialFileInputRef}
              handleFileChange={handleFileChange}
              materialSearchTerm={materialSearchTerm}
              setMaterialSearchTerm={setMaterialSearchTerm}
              filteredMaterials={filteredMaterials}
              editingMaterialId={editingMaterialId}
              editingMaterialName={editingMaterialName}
              setEditingMaterialName={setEditingMaterialName}
              handleSaveMaterialName={handleSaveMaterialName}
              handleCancelEditMaterial={handleCancelEditMaterial}
              handleMaterialPreview={handleMaterialPreview}
              handleEditMaterialName={handleEditMaterialName}
              handleDeleteMaterial={handleDeleteMaterial}
            />
          )}
        </div>
      </div>

      <div className="supplementary-sections-container">
        <div className="section-card upcoming-lessons-card">
          <UpcomingLessonsTab
            groupId={groupId}
            groupData={groupData}
            weekOffset={weekOffset}
            setWeekOffset={setWeekOffset}
            selectedDayIndex={selectedDayIndex}
            setSelectedDayIndex={setSelectedDayIndex}
            handleLessonStatusChange={handleUpcomingLessonStatusChange}
          />
        </div>
      </div>


      {/* Custom Modal */}
      {showCustomModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">
            <div className={`custom-modal-header ${customModalConfig.type}`}>
              <h3>{customModalConfig.title}</h3>
              <button className="custom-modal-close" onClick={hideCustomModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="custom-modal-body">
              <p>{customModalConfig.message}</p>
            </div>
            <div className="custom-modal-footer">
              {customModalConfig.showCancel && (
                <button className="custom-modal-btn cancel-btn" onClick={hideCustomModal}>
                  {customModalConfig.cancelText}
                </button>
              )}
              <button className={`custom-modal-btn confirm-btn ${customModalConfig.type}`} onClick={handleModalConfirm}>
                {customModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Homework Modal */}
      <HomeworkModal selectedHomework={selectedHomework} handleCloseHomework={handleCloseHomework} handleCheckHomework={handleCheckHomework} />

      {/* Floating action buttons - fixed to viewport */}
      <div className="fab-stack">
        <div className="fab-col">
          <button className="fab-btn assessment" title="Progress Assessment">
            <svg height="24" viewBox="0 0 59 59" width="24" xmlns="http://www.w3.org/2000/svg">
              <g id="Layer_2" data-name="Layer 2">
                <g id="Layer_1-2" data-name="Layer 1">
                  <g id="info">
                    <path d="m29.5 0a29.5 29.5 0 1 0 29.5 29.5 29.54 29.54 0 0 0 -29.5-29.5zm2.5 41.5a2.5 2.5 0 0 1 -5 0v-14a2.5 2.5 0 0 1 5 0zm0-22.83a2.5 2.5 0 0 1 -5 0v-1.17a2.5 2.5 0 0 1 5 0z" fill="currentColor"/>
                  </g>
                </g>
              </g>
            </svg>
          </button>
          <button className="fab-btn chat" title="Chat Now">
            <svg fill="none" height="24" viewBox="0 0 64 64" width="24" xmlns="http://www.w3.org/2000/svg">
              <path clip-rule="evenodd" d="m2 16c0-7.73199 6.26801-14 14-14h32c7.732 0 14 6.26801 14 14v44c0 .7523-.4221 1.4409-1.0924 1.7822-.6704.3414-1.4756.2777-2.0839-.1647l-10.4741-7.6175h-32.3496c-7.73199 0-14-6.268-14-14zm15 2c-1.1046 0-2 .8954-2 2s.8954 2 2 2h30c1.1046 0 2-.8954 2-2s-.8954-2-2-2zm0 12c-1.1046 0-2 .8954-2 2s.8954 2 2 2h12c1.1046 0 2-.8954 2-2s-.8954-2-2-2z" fill="currentColor" fill-rule="evenodd"/>
            </svg>
          </button>
        </div>
        <button className="fab-main create-lesson-btn" onClick={handleCreateLesson}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Student Selector Modal */}
      {showStudentSelector && (
        <div className="custom-modal-overlay">
          <div className="custom-modal student-selector-modal">
            <div className="custom-modal-header">
              <h3>Select Students for Group</h3>
              <button className="custom-modal-close" onClick={() => setShowStudentSelector(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="custom-modal-body">
              <div className="student-selector-list">
                {Object.entries(getStudentData()).map(([studentId, student]) => (
                  <div 
                    key={studentId} 
                    className={`student-selector-item ${participants.find(p => p.id === studentId) ? 'selected' : ''}`}
                    onClick={() => handleAddParticipant(studentId)}
                  >
                    <div className="student-selector-info">
                      <div className="student-selector-name">{student.name}</div>
                      <div className="student-selector-details">
                        <span>{student.contactEmail}</span>
                        <span>{student.contactPhone}</span>
                      </div>
                    </div>
                    {participants.find(p => p.id === studentId) && (
                      <div className="student-selector-check">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="custom-modal-footer">
              <button className="custom-modal-btn confirm-btn" onClick={() => setShowStudentSelector(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <CopyLessonModal
        showCopyModal={showCopyModal}
        setShowCopyModal={setShowCopyModal}
        lessonToCopy={lessonToCopy}
        copyTargetStudent={copyTargetStudent}
        setCopyTargetStudent={setCopyTargetStudent}
        copyModalGroups={copyModalGroups}
        copyDate={copyDate}
        setCopyDate={setCopyDate}
        copyLessonSubmitting={copyLessonSubmitting}
        confirmCopyLesson={confirmCopyLesson}
      />

      <MaterialPreviewModal previewMaterial={previewMaterial} handleClosePreview={handleClosePreview} />
    </div>
  );
}

export default GroupProfile; 